import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../shared/api/client';
import { applyLocale, isUiLocale, useLocaleStore, type UiLocale } from './index';

interface I18nConfig {
  enabledLocales: UiLocale[];
  defaultLocale: string;
}

/**
 * The dictionaries ship with the panel: one JSON file per language in
 * `./locales`, the English text as the key. Adding a language is adding a
 * file — nothing to seed, nothing to edit in a console, and a translation
 * is reviewed in a pull request like any other change.
 *
 * `import.meta.glob` leaves each file in its own chunk, so a reader on
 * English never downloads Hindi.
 */
const DICTIONARIES = import.meta.glob<{ default: Record<string, string> }>('./locales/*.json');

function loaderFor(locale: UiLocale): (() => Promise<{ default: Record<string, string> }>) | undefined {
  return DICTIONARIES[`./locales/${locale}.json`];
}

/** The languages that actually have a dictionary in the bundle, English included. */
const TRANSLATED: readonly UiLocale[] = ['en', ...Object.keys(DICTIONARIES).map((path) => path.slice('./locales/'.length, -'.json'.length)).filter(isUiLocale)];

/**
 * Resolves the language the panel should show — the reader's choice if the
 * platform offers it, else the platform's default — loads its dictionary and
 * applies it. Returns a `key` the shell puts on the page so everything
 * already drawn is drawn again in the new language.
 */
export function useLocaleSetting(): { locale: UiLocale; enabled: UiLocale[]; key: string; ready: boolean } {
  const chosen = useLocaleStore((state) => state.locale);
  const config = useQuery({
    queryKey: ['public', 'i18n', 'config'],
    queryFn: () => api.get<I18nConfig>('/public/i18n', { anonymous: true }),
    staleTime: 60_000,
    retry: false,
  });
  // A language the platform has switched on but that has no dictionary yet
  // would be a switch that changes nothing, so it is not offered.
  const enabled = React.useMemo<UiLocale[]>(() => {
    const offered = config.data?.enabledLocales.filter(isUiLocale) ?? ['en'];
    const list = TRANSLATED.filter((locale) => offered.includes(locale));
    return list.includes('en') ? list : ['en', ...list];
  }, [config.data]);
  const fallback = config.data && isUiLocale(config.data.defaultLocale) && enabled.includes(config.data.defaultLocale) ? config.data.defaultLocale : 'en';
  const locale: UiLocale = chosen && enabled.includes(chosen) ? chosen : fallback;

  const dictionary = useQuery({
    queryKey: ['i18n', 'dictionary', locale],
    queryFn: async () => (await loaderFor(locale)?.())?.default ?? {},
    enabled: locale !== 'en',
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const ready = locale === 'en' || dictionary.data !== undefined;
  // Applied during render so the re-keyed page draws with the new dictionary
  // in the same pass; an effect would leave it one render behind.
  if (ready) applyLocale(locale, dictionary.data ?? {});

  const key = `${locale}:${ready ? 'ready' : 'loading'}`;
  return { locale, enabled, key, ready };
}
